var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState, useEffect } from 'react';
import shortid from 'shortid';
import { useIsMobile } from '../../utils';
import styled, { css } from '../../styled';
import { Alert } from '../icons/solid';
import { RightIconSizeMap } from '../../themes/inputs';
import TooltipView, { Content } from '../tooltip/TooltipView';
export const RightIconWrapper = styled.span `
  padding: ${({ inputSize, theme }) => theme.inputs.size[inputSize].rightIconPadding};
  position: absolute;
  right: 0;
  bottom: 0;
  line-height: 0;
  > svg {
    * {
      ${props => props.status === 'valid' &&
    css `
          fill: ${({ theme }) => theme.colors.INK_400};
        `}

      ${props => props.status === 'invalid' &&
    css `
          fill: ${({ theme }) => theme.colors.RED_600};
        `}
    }
  }
`;
const LeftIconWrapper = styled.span `
  padding: ${({ inputSize, theme }) => theme.inputs.size[inputSize].leftIconPadding};
  position: absolute;
  left: 0;
  bottom: 0;
  line-height: 0;
  > svg {
    * {
      fill: ${({ theme }) => theme.colors.INK_400};
    }
  }
`;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const generateStatusStyle = (status, isStateless = false, isDisabled = false, isFocus = false) => {
    if (status === 'invalid') {
        return css `
      &${!isStateless && ':enabled'} {
        border: solid 0.0625rem
          ${({ theme }) => theme.colors[theme.inputs.status.invalid.borderColor]};
        background-color: ${({ theme }) => theme.colors[theme.inputs.status.invalid.backgroundColor]};
        color: ${({ theme }) => theme.colors[theme.inputs.status.invalid.textColor]};
      }

      ${!isDisabled &&
            css `
        &:hover${!isStateless && ':enabled'},
          &${!isFocus && ':focus'}${!isStateless && ':enabled'} {
          border: solid 0.0625rem
            ${({ theme }) => theme.colors[theme.inputs.status.invalidHover.borderColor]};
          background-color: ${({ theme }) => theme.colors[theme.inputs.status.invalidHover.backgroundColor]};
          color: ${({ theme }) => theme.colors[theme.inputs.status.invalidHover.textColor]};

          ~ ${RightIconWrapper} {
            * {
              fill: ${({ theme }) => theme.colors[theme.inputs.status.invalidHover.borderColor]};
            }
          }
        }
      `}

      ${(!isStateless || isDisabled) &&
            css `
        &${!isStateless && !isDisabled && ':disabled'} {
          border: solid 0.0625rem
            ${({ theme }) => theme.colors[theme.inputs.status.disabled.borderColor]};
          background-color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.backgroundColor]};
          color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.textColor]};
          // Override -webkit-text-fill-color for font color
          &::placeholder {
            -webkit-text-fill-color: ${({ theme }) => theme.colors.INK_400};
          }
          cursor: not-allowed;

          // Override Safari disabled input font color
          -webkit-opacity: 1;
          -webkit-text-fill-color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.textColor]};
        }
      `}
    `;
    }
    return css `
    &${!isStateless && ':enabled'} {
      border: solid 0.0625rem
        ${({ theme }) => theme.colors[theme.inputs.status.default.borderColor]};
      background-color: ${({ theme }) => theme.colors[theme.inputs.status.default.backgroundColor]};
      color: ${({ theme }) => theme.colors[theme.inputs.status.default.textColor]};
    }

    ${!isDisabled &&
        css `
      &:hover${!isStateless && ':enabled'},
        &${!isFocus && ':focus'}${!isStateless && ':enabled'} {
        border: solid 0.0625rem
          ${({ theme }) => theme.colors[theme.inputs.status.hover.borderColor]};
        background-color: ${({ theme }) => theme.colors[theme.inputs.status.hover.backgroundColor]};
        color: ${({ theme }) => theme.colors[theme.inputs.status.hover.textColor]};

        ~ ${LeftIconWrapper} {
          * {
            fill: ${({ theme }) => theme.colors[theme.inputs.status.hover.borderColor]};
          }
        }
      }
    `}

    ${(!isStateless || isDisabled) &&
        css `
      &${!isStateless && !isDisabled && ':disabled'} {
        border: solid 0.0625rem
          ${({ theme }) => theme.colors[theme.inputs.status.disabled.borderColor]};
        background-color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.backgroundColor]};
        color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.textColor]};
        // Override -webkit-text-fill-color for font color
        &::placeholder {
          -webkit-text-fill-color: ${({ theme }) => theme.colors.INK_400};
        }
        cursor: not-allowed;

        // Override Safari disabled input font color
        -webkit-opacity: 1;
        -webkit-text-fill-color: ${({ theme }) => theme.colors[theme.inputs.status.disabled.textColor]};
      }
    `}
  `;
};
export const Wrapper = styled.div `
  display: ${({ block }) => (block ? 'flex' : 'inline-flex')};
  outline: 0;
  padding: 0 0.5rem;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  ${({ status, disabled, focused }) => generateStatusStyle(status, true, disabled, focused)};

  input {
    padding: 0 0.25rem;
    color: inherit;
    border: none !important;
  }
  input::placeholder {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;
const PreTextWrapper = styled.div `
  display: flex;
  align-items: center;
  font-size: ${({ inputSize, theme }) => theme.inputs.size[inputSize].fontSize};
  color: ${({ theme }) => theme.colors.INK_500}};
  white-space: nowrap;
`;
export const StyledInput = styled.input `
  outline: none;
  padding: ${({ inputSize, theme }) => theme.inputs.size[inputSize].padding};
  border-radius: 6px;
  font-size: ${({ inputSize, theme }) => theme.inputs.size[inputSize].fontSize};
  width: ${({ width }) => width || 10}rem;
  height: ${({ inputSize, theme }) => theme.inputs.size[inputSize].height};
  box-sizing: border-box;
  overflow: hidden;
  text-align: ${({ textAlignRight }) => (textAlignRight ? 'right' : 'left')};
  text-overflow: ellipsis;
  white-space: nowrap;
  &::placeholder {
    color: ${({ theme }) => theme.colors.INK_400};
  }

  ${({ status }) => generateStatusStyle(status)};
  ${({ hideBorder }) => hideBorder &&
    css `
      border: none;
    `}

  ${props => props.block &&
    css `
      display: block;
      width: 100%;
    `};

  ${props => props.haveLeftIcon &&
    css `
      padding-right: 2rem;
    `};

  ${props => props.haveRightIcon &&
    css `
      padding-left: ${({ inputSize, theme }) => theme.inputs.size[inputSize].containerRightIconPadding};
    `};
`;
export const InputWrapper = styled.div `
  position: relative;
  display: inline-flex;

  .tooltip-container {
    display: inherit;
    flex: 1;
    cursor: unset;

    ${Content} {
      flex: 1;
    }
  }

  ${props => props.block &&
    css `
      display: flex;
      width: 100%;
    `}
`;
export const Title = styled.div `
  color: ${({ theme }) => theme.colors.INK_800};
  font-size: 0.875rem;
  padding-bottom: 0.25rem;
`;
export const MessageWrapper = styled.div `
  padding: 0.25rem 0;
`;
export const Message = styled.div `
  font-size: 0.8125rem;
  line-height: 1.23;
  padding: 0;
  ${props => props.status === 'invalid' &&
    css `
      color: ${({ theme }) => theme.colors.RED_600};
    `}
  ${props => props.status === 'valid' &&
    css `
      color: ${({ theme }) => theme.colors.INK_500};
    `}
  ${({ withBullet }) => withBullet &&
    css `
      &:before {
        content: '•';
        color: ${({ theme }) => theme.colors.RED_600};
        font-weight: bold;
        display: inline-block;
        padding-right: 0.25rem;
      }
    `}
`;
const Input = (_a) => {
    var { title, disabled, status = 'valid', value, defaultValue, renderMessages = [], innerRef = React.createRef(), block = false, Icon, inputSize = 'MEDIUM', preTextPosition = 'LEFT', renderPreText, onChange, e2eInputId, e2eMessagesId } = _a, restProps = __rest(_a, ["title", "disabled", "status", "value", "defaultValue", "renderMessages", "innerRef", "block", "Icon", "inputSize", "preTextPosition", "renderPreText", "onChange", "e2eInputId", "e2eMessagesId"]);
    const [tooltipText, setTooltipText] = useState(defaultValue || value);
    const [isFocus, setIsFocus] = useState(false);
    const [isTooltipOverflow, setIsTooltipOverflow] = useState(false);
    const [isTooltipHover, setIsTooltipHover] = useState(false);
    const isMobile = useIsMobile();
    const updateTooltipOverflowStatus = useCallback(() => {
        if (typeof innerRef === 'function')
            return;
        if (innerRef && innerRef.current) {
            // 1 for width buffer
            const isOverflow = innerRef.current.scrollWidth > innerRef.current.clientWidth + 1;
            if (isTooltipOverflow !== isOverflow) {
                // only setState when state changes
                setIsTooltipOverflow(isOverflow);
            }
        }
    }, [innerRef, isTooltipOverflow]);
    const handleBlur = () => {
        setIsFocus(false);
    };
    const handleMouseEnter = () => {
        if (typeof innerRef === 'function')
            return;
        if (innerRef && innerRef.current) {
            setIsTooltipHover(true);
        }
    };
    const handleMouseLeave = () => {
        if (typeof innerRef === 'function')
            return;
        if (innerRef && innerRef.current) {
            setIsTooltipHover(false);
        }
    };
    const handleWrapperClick = useCallback(() => {
        if (typeof innerRef === 'function')
            return;
        if (innerRef && innerRef.current) {
            setIsFocus(true);
            innerRef.current.focus();
        }
    }, [innerRef]);
    useEffect(() => {
        if (typeof innerRef === 'function')
            return undefined;
        if (!innerRef || !innerRef.current)
            return undefined;
        const element = innerRef.current;
        const cb = () => {
            setIsFocus(true);
            setTooltipText(element.value);
        };
        // init
        updateTooltipOverflowStatus();
        element.addEventListener('keyup', cb);
        element.addEventListener('blur', handleBlur);
        return () => {
            element.removeEventListener('keyup', cb);
            element.removeEventListener('blur', handleBlur);
        };
    }, [innerRef, updateTooltipOverflowStatus]);
    const preText = renderPreText && (React.createElement(PreTextWrapper, { inputSize: inputSize }, renderPreText()));
    const input = (React.createElement(StyledInput, Object.assign({ ref: innerRef, defaultValue: defaultValue, value: value, status: status, disabled: disabled, inputSize: inputSize, block: block, haveRightIcon: !!Icon, haveLeftIcon: status === 'invalid', hideBorder: !!renderPreText, textAlignRight: !!renderPreText && preTextPosition === 'LEFT', onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onChange: event => {
            updateTooltipOverflowStatus();
            if (onChange)
                onChange(event);
        } }, restProps)));
    const titles = title && React.createElement(Title, null, title);
    const content = (React.createElement(React.Fragment, null,
        preTextPosition === 'LEFT' && preText,
        React.createElement(InputWrapper, { "data-e2e-id": e2eInputId, block: block },
            renderPreText ? (React.createElement(TooltipView, { type: "primary", lang: "en", text: tooltipText ? tooltipText.toString() : '', show: isTooltipOverflow && (isMobile ? isFocus : isTooltipHover), direction: preTextPosition === 'LEFT' ? 'TOP_RIGHT' : 'TOP_LEFT', containerClassName: "tooltip-container" }, input)) : (input),
            Icon && (React.createElement(LeftIconWrapper, { inputSize: inputSize },
                React.createElement(Icon, { size: "MEDIUM" }))),
            !renderPreText && status === 'invalid' && (React.createElement(RightIconWrapper, { status: status, inputSize: inputSize },
                React.createElement(Alert, { size: RightIconSizeMap[inputSize] })))),
        preTextPosition === 'RIGHT' && preText));
    const messages = (React.createElement(MessageWrapper, { "data-e2e-id": e2eMessagesId }, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))));
    if (renderPreText) {
        return (React.createElement(React.Fragment, null,
            titles,
            React.createElement(Wrapper, { status: status, disabled: disabled, focused: isFocus, onClick: handleWrapperClick, onBlur: handleBlur, block: block }, content),
            messages));
    }
    return (React.createElement("div", null,
        titles,
        content,
        messages));
};
export default React.memo(React.forwardRef((props, ref) => (React.createElement(Input, Object.assign({ innerRef: ref }, props)))));
//# sourceMappingURL=index.js.map